import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/axios'
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  BellAlertIcon,
  XCircleIcon,
  ChartBarIcon,
  ArrowRightIcon,
  ServerStackIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'

interface DashboardStats {
  totalCompanies: number
  activeCompanies: number
  trialCompanies: number
  suspendedCompanies: number
  pendingCompanies: number
  tierDistribution: { basic: number; standard: number; premium: number; enterprise: number }
  systemHealth: {
    status: string
    uptime: number
    memory: { percentUsed: number; used: number; total: number }
    cpu: { count: number; loadAverage: number[] }
    database: { status: string; responseTime?: number; activeConnections?: number }
  } | null
  activeAlerts: number
  recentActivity: Array<{
    id: string; admin: string; action: string; resource: string; resourceName: string; timestamp: string
  }>
}

const CACHE_KEY = 'dashboard-stats-cache'
const CACHE_TTL = 60000

const TIER_COLORS: Record<string, string> = {
  enterprise: 'bg-violet-500',
  premium: 'bg-blue-500',
  standard: 'bg-slate-500',
  basic: 'bg-slate-400',
}
const TIER_TEXT: Record<string, string> = {
  enterprise: 'text-violet-700 bg-violet-100',
  premium: 'text-blue-700 bg-blue-100',
  standard: 'text-slate-700 bg-slate-100',
  basic: 'text-slate-600 bg-slate-100',
}

function StatCard({ label, value, sub, icon: Icon, iconClass, accentClass, onClick }: {
  label: string; value: number | string; sub?: string
  icon: React.ComponentType<{ className?: string }>
  iconClass: string; accentClass: string; onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 transition-all hover:shadow-md ${onClick ? 'cursor-pointer hover:border-blue-200' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1.5">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accentClass}`}>
          <Icon className={`w-5 h-5 ${iconClass}`} />
        </div>
      </div>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-3 bg-slate-200 rounded w-24 mb-3" />
          <div className="h-8 bg-slate-200 rounded w-16" />
        </div>
        <div className="w-11 h-11 bg-slate-200 rounded-xl" />
      </div>
    </div>
  )
}

export default function EnhancedDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    const cached = getCachedStats()
    if (cached) {
      setStats(cached)
      setLoading(false)
      fetchStats(true)
    } else {
      fetchStats(false)
    }
    const interval = setInterval(() => refreshStats(), 120000)
    return () => clearInterval(interval)
  }, [])

  const getCachedStats = (): DashboardStats | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null
      const { data, timestamp } = JSON.parse(cached)
      if (Date.now() - timestamp > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return null }
      return data
    } catch { return null }
  }

  const setCachedStats = (data: DashboardStats) => {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() })) } catch {}
  }

  const fetchStats = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const response = await api.get('/super-admin/dashboard/stats')
      setStats(response.data)
      setCachedStats(response.data)
      setLastUpdated(new Date())
      setError('')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard statistics')
    } finally {
      setLoading(false)
    }
  }

  const refreshStats = async () => {
    try {
      setRefreshing(true)
      const response = await api.get('/super-admin/dashboard/stats')
      setStats(response.data)
      setCachedStats(response.data)
      setLastUpdated(new Date())
    } catch (err: any) {
      console.error('Auto-refresh failed:', err)
    } finally {
      setRefreshing(false)
    }
  }

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hours}h ${mins}m`
  }

  const formatBytes = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024)
    if (gb >= 1) return gb.toFixed(1) + ' GB'
    return (bytes / (1024 * 1024)).toFixed(0) + ' MB'
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="h-7 bg-slate-200 rounded w-40 mb-2 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-56 animate-pulse" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      </div>
    )
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md w-full text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Failed to load dashboard</h2>
          <p className="text-sm text-slate-500 mb-6">{error}</p>
          <button onClick={() => fetchStats(false)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">Try Again</button>
        </div>
      </div>
    )
  }

  if (!stats) return null

  const activeRate = stats.totalCompanies > 0 ? Math.round((stats.activeCompanies / stats.totalCompanies) * 100) : 0
  const memPct = stats.systemHealth?.memory.percentUsed ?? 0
  const dbStatus = stats.systemHealth?.database.status ?? 'unknown'
  const healthOk = dbStatus === 'healthy' && memPct < 80

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Platform overview
              {lastUpdated && <span className="ml-1">· Updated {lastUpdated.toLocaleTimeString()}</span>}
            </p>
          </div>
          <button
            onClick={refreshStats}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">

        {/* Alert banners */}
        {stats.activeAlerts > 0 && (
          <button
            onClick={() => navigate('/monitoring')}
            className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-left hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <BellAlertIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <span className="text-sm font-semibold text-red-800">{stats.activeAlerts} active alert{stats.activeAlerts !== 1 ? 's' : ''} require attention</span>
                <p className="text-xs text-red-600 mt-0.5">Click to view and resolve in System Monitoring</p>
              </div>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
          </button>
        )}

        {(stats.pendingCompanies ?? 0) > 0 && (
          <button
            onClick={() => navigate('/companies')}
            className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-left hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <span className="text-sm font-semibold text-amber-800">{stats.pendingCompanies} compan{stats.pendingCompanies !== 1 ? 'ies' : 'y'} pending setup</span>
                <p className="text-xs text-amber-600 mt-0.5">Stack Auth not yet configured — click to review</p>
              </div>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          </button>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label="Total Companies"
            value={stats.totalCompanies}
            icon={BuildingOfficeIcon}
            iconClass="text-blue-600"
            accentClass="bg-blue-50"
            onClick={() => navigate('/companies')}
          />
          <StatCard
            label="Active"
            value={stats.activeCompanies}
            sub={`${activeRate}% of total`}
            icon={CheckCircleIcon}
            iconClass="text-emerald-600"
            accentClass="bg-emerald-50"
            onClick={() => navigate('/companies')}
          />
          <StatCard
            label="On Trial"
            value={stats.trialCompanies}
            sub="Active trial accounts"
            icon={ClockIcon}
            iconClass="text-amber-600"
            accentClass="bg-amber-50"
            onClick={() => navigate('/signups')}
          />
          <StatCard
            label="Suspended"
            value={stats.suspendedCompanies}
            icon={XCircleIcon}
            iconClass="text-red-500"
            accentClass="bg-red-50"
            onClick={() => navigate('/companies')}
          />
          <StatCard
            label="Active Alerts"
            value={stats.activeAlerts}
            icon={BellAlertIcon}
            iconClass={stats.activeAlerts > 0 ? 'text-red-600' : 'text-slate-400'}
            accentClass={stats.activeAlerts > 0 ? 'bg-red-50' : 'bg-slate-50'}
            onClick={() => navigate('/monitoring')}
          />
        </div>

        {/* Middle Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Subscription Mix */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-900">Subscription Mix</h2>
              <ChartBarIcon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="space-y-3">
              {Object.entries(stats.tierDistribution).map(([tier, count]) => {
                const pct = stats.totalCompanies > 0 ? Math.round((count / stats.totalCompanies) * 100) : 0
                return (
                  <div key={tier}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${TIER_TEXT[tier]}`}>{tier}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{count} <span className="text-slate-400 font-normal text-xs">({pct}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${TIER_COLORS[tier] || 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-900">System Health</h2>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                healthOk ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${healthOk ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                {stats.systemHealth?.status ?? 'Unknown'}
              </span>
            </div>
            {stats.systemHealth ? (
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500 flex items-center gap-1.5"><ServerStackIcon className="w-3 h-3" />Memory</span>
                    <span className="font-semibold text-slate-700">{memPct.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${memPct > 80 ? 'bg-red-500' : memPct > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${memPct}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">{formatBytes(stats.systemHealth.memory.used)} of {formatBytes(stats.systemHealth.memory.total)}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Uptime</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{formatUptime(stats.systemHealth.uptime)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">DB Latency</p>
                    <p className={`text-sm font-bold mt-0.5 ${dbStatus === 'healthy' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {stats.systemHealth.database.responseTime ? `${stats.systemHealth.database.responseTime}ms` : dbStatus}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">CPU Cores</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{stats.systemHealth.cpu.count}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Load Avg</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{stats.systemHealth.cpu.loadAverage[0]?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No health data available</p>
            )}
            <button onClick={() => navigate('/monitoring')} className="mt-4 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">
              View full monitoring <ArrowRightIcon className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { label: 'Manage Companies', sub: `${stats.totalCompanies} total`, path: '/companies', icon: BuildingOfficeIcon, color: 'text-blue-600 bg-blue-50' },
                { label: 'Signups & trials', sub: `${stats.trialCompanies} on trial`, path: '/signups', icon: ClockIcon, color: 'text-amber-600 bg-amber-50' },
                { label: 'System Monitoring', sub: stats.activeAlerts > 0 ? `${stats.activeAlerts} alerts` : 'All clear', path: '/monitoring', icon: CpuChipIcon, color: stats.activeAlerts > 0 ? 'text-red-600 bg-red-50' : 'text-emerald-600 bg-emerald-50' },
                { label: 'Reports & Analytics', sub: 'View platform data', path: '/reports', icon: ChartBarIcon, color: 'text-violet-600 bg-violet-50' },
                { label: 'Feature Flags', sub: 'Per-company features', path: '/features', icon: ShieldCheckIcon, color: 'text-slate-600 bg-slate-100' },
              ].map(({ label, sub, path, icon: Icon, color }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-blue-600 transition-colors">{label}</p>
                    <p className="text-xs text-slate-400">{sub}</p>
                  </div>
                  <ArrowRightIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-400 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Recent Admin Activity</h2>
            <span className="text-xs text-slate-400">{stats.recentActivity.length} entries</span>
          </div>
          {stats.recentActivity.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400">No recent activity</div>
          ) : (
            <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
              {stats.recentActivity.map((activity) => (
                <div key={activity.id} className="px-6 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                  <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-blue-600">{activity.admin.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold text-slate-900">{activity.admin.split('@')[0]}</span>
                      {' '}{activity.action}{' '}{activity.resource}
                      {activity.resourceName && <span className="text-slate-500"> "{activity.resourceName}"</span>}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{new Date(activity.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
